import { useQuery } from '@apollo/client';
import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import {
  NoSearchResults,
  ProfileDrawer,
  ProfileList,
  type Market,
  type ProfileCardView,
  SkeletonLoader,
  ProfilePanel,
} from '@ts-internal/shared-lib';
import { useTranslations } from '@vocab/react';
import { Box, Stack } from 'braid-design-system';
import { useCallback, useMemo, useState } from 'react';

import { mapProfileSearchForDisplay } from 'src/App/services/profile/mapProfileSearchForDisplay';
import { useSubmitProfileReport } from 'src/App/services/profileReport/useSubmitProfileReport';
import { defaultPageSize } from 'src/App/services/search/searchDefaults';
import { useHubbleClient } from 'src/App/services/tracking/useHubbleClient';
import { useLoginPromptTracking } from 'src/App/services/tracking/useLoginPromptTracking';
import {
  PublicProfileViewDocument,
  type PublicProfileSearchQuery,
} from 'src/generated/graphql';

import { mapProfileViewForDisplay } from '../../services/profile/mapProfileViewForDisplay';
import { getLoginPromptVisibility } from '../../services/search/getLoginPromptVisibility';
import { getPaginationVisibility } from '../../services/search/getPaginationVisibility';
import { LoginPromptBanner } from '../LoginPrompt';

import translations from './.vocab';

interface Props {
  isLoading: boolean;
  profileSearchResults: PublicProfileSearchQuery['publicProfileSearch'];
  authUrls: {
    loginUrl: string;
    googleLoginUrl: string;
    registerUrl: string;
  };
  isAuthenticated: boolean;
  pageNumber: number;
  handlePageChange?: (pageNumber: number) => void;
  onProfileClick?: (profileId: string, serviceToken: string) => void;
  market: Market;
  onLoginNavigate?: (url: string) => void;
}

export const SearchResult = ({
  isLoading,
  authUrls: { loginUrl, registerUrl, googleLoginUrl },
  profileSearchResults,
  isAuthenticated,
  pageNumber,
  handlePageChange,
  onLoginNavigate,
}: Props) => {
  const { t } = useTranslations(translations);
  const { locale } = useMelwaysInfo();
  const localize = useMelwaysLink();
  const {
    exactMatchesProfiles,
    partialMatchesProfiles,
    totalExactMatches,
    totalPartialMatches,
    totalMatches,
    serviceToken: candidateDiscoverySourceId,
  } = mapProfileSearchForDisplay(profileSearchResults);

  const { showLoginInExactMatchList, showLoginInPossibleMatchList } =
    getLoginPromptVisibility({
      isAuthenticated,
      totalMatches,
      totalExactMatches,
      itemsPerPage: defaultPageSize,
    });

  const hasExactMatches = exactMatchesProfiles.length > 0;
  const hasPossibleMatches = partialMatchesProfiles.length > 0;
  const hasNoMatches = totalMatches === 0;

  const bannerTrackingHook = useLoginPromptTracking(
    'people search',
    'search results',
  );

  const searchResultLoginTrackingHook = useLoginPromptTracking(
    'people search',
    'profile summary',
    'linkOut',
  );

  const profileDrawerTrackingHook = useLoginPromptTracking(
    'profile drawer',
    'page content',
  );

  const { submitProfileReport } = useSubmitProfileReport();

  const searchResultLoginTrackingCallbacks = useMemo(
    () => ({
      onComponentDisplayed:
        searchResultLoginTrackingHook.trackComponentDisplayed,
      onLoginPressed: searchResultLoginTrackingHook.trackLoginPressed,
      onAuthLinkPressed: searchResultLoginTrackingHook.trackAuthLinkPressed,
    }),
    [
      searchResultLoginTrackingHook.trackComponentDisplayed,
      searchResultLoginTrackingHook.trackLoginPressed,
      searchResultLoginTrackingHook.trackAuthLinkPressed,
    ],
  );

  const bannerTrackingCallbacks = useMemo(
    () => ({
      onComponentDisplayed: bannerTrackingHook.trackComponentDisplayed,
      onLoginPressed: bannerTrackingHook.trackLoginPressed,
      onAuthLinkPressed: bannerTrackingHook.trackAuthLinkPressed,
    }),
    [
      bannerTrackingHook.trackComponentDisplayed,
      bannerTrackingHook.trackLoginPressed,
      bannerTrackingHook.trackAuthLinkPressed,
    ],
  );

  const profileDrawerTrackingCallbacks = useMemo(
    () => ({
      onComponentDisplayed: profileDrawerTrackingHook.trackComponentDisplayed,
      onLoginPressed: profileDrawerTrackingHook.trackLoginPressed,
      onAuthLinkPressed: profileDrawerTrackingHook.trackAuthLinkPressed,
    }),
    [
      profileDrawerTrackingHook.trackComponentDisplayed,
      profileDrawerTrackingHook.trackLoginPressed,
      profileDrawerTrackingHook.trackAuthLinkPressed,
    ],
  );

  const InListLoginPrompt = (
    <LoginPromptBanner
      hasBlurryBackground
      loginUrl={loginUrl}
      googleLoginUrl={googleLoginUrl}
      registerUrl={registerUrl}
      trackingCallbacks={{
        onComponentDisplayed: bannerTrackingCallbacks.onComponentDisplayed,
        onLoginPressed: bannerTrackingCallbacks.onLoginPressed,
        onAuthLinkPressed: bannerTrackingCallbacks.onAuthLinkPressed,
      }}
    />
  );

  const totalPages = totalMatches
    ? Math.ceil(totalMatches / defaultPageSize)
    : 1;

  const [selectedProfileSlug, setSelectedProfileSlug] = useState<string | null>(
    null,
  );

  // Pagination
  const { showPaginationInExactMatchList, showPaginationInPossibleMatchList } =
    getPaginationVisibility({
      isAuthenticated,
      totalMatches,
      totalExactMatches,
      itemsPerPage: defaultPageSize,
    });

  const paginationProps = {
    page: pageNumber,
    total: totalPages,
    label: 'PAGINATION_OF_RESULTS',
    linkProps: ({ page }: { page: number }) => ({
      href: '#',
      onClick: (e: any) => {
        e.preventDefault();
        handlePageChange?.(page);
      },
    }),
  };

  const handleProfileClick = (profileSlug: string) => {
    setSelectedProfileSlug(profileSlug);
  };

  const handleDrawerClose = () => {
    setSelectedProfileSlug(null);
  };

  const {
    data,
    error: profileViewError,
    loading: profileViewLoading,
  } = useQuery(PublicProfileViewDocument, {
    variables: {
      input: {
        slug: selectedProfileSlug ?? '',
      },
      locale,
    },
    skip: !selectedProfileSlug,
  });

  const getProfileViewLink = (profile: ProfileCardView) =>
    localize({
      path: `/profiles/${profile.slug}?serviceToken=${profile.serviceToken}&tracking=PROFILE_SEARCH`,
    });

  const hubble = useHubbleClient();

  const mappedProfile = mapProfileViewForDisplay(data?.publicProfileView);

  const profileDisplayedCallback = useCallback(() => {
    const candidateDiscoverySource = mappedProfile?.isOwnedByLoggedInUser
      ? 'OWN_PROFILE'
      : 'NAME_SEARCH';

    hubble.trackCandidateProfileDisplayedEvent({
      currentPage: 'profile drawer',
      profileGuid: mappedProfile?.profileGuid,
      candidateDiscoverySource,
      ...(candidateDiscoverySourceId && {
        candidateDiscoverySourceId,
      }),
    });
  }, [
    mappedProfile?.isOwnedByLoggedInUser,
    hubble,
    mappedProfile?.profileGuid,
    candidateDiscoverySourceId,
  ]);

  const getPossibleMatchesCountText = () => {
    if (totalPartialMatches === 0) {
      return '';
    }

    const onlyPossibleMatches = totalExactMatches === 0;

    if (onlyPossibleMatches) {
      return t(
        'COULD_NOT_FIND_AN_EXACT_MATCH_HERE_ARE_PROFILES_YOU_MIGHT_BE_INTERESTED_IN',
        {
          count: totalPartialMatches,
        },
      );
    }

    return t('X_PROFILES_YOU_MIGHT_BE_INTERESTED_IN', {
      count: totalPartialMatches,
    });
  };

  const trackingCallbacks = {
    profileDisplayedCallback,
    onComponentDisplayed: profileDrawerTrackingCallbacks.onComponentDisplayed,
    onLoginPressed: profileDrawerTrackingCallbacks.onLoginPressed,
    onAuthLinkPressed: profileDrawerTrackingCallbacks.onAuthLinkPressed,
  };

  const profileUrl = selectedProfileSlug
    ? localize({ path: `/profiles/${selectedProfileSlug}` })
    : undefined;

  return (
    <SkeletonLoader isLoading={isLoading}>
      <Stack space="small">
        <ProfileDrawer
          isOpen={Boolean(selectedProfileSlug)}
          onClose={handleDrawerClose}
          submitProfileReport={submitProfileReport}
          isAuthenticated={isAuthenticated}
          isLoading={profileViewLoading}
          hasError={Boolean(profileViewError)}
          profile={mappedProfile}
          profileUrl={profileUrl}
          tabs={
            mappedProfile
              ? [
                  {
                    title: t('PROFILE'),
                    key: 'profile',
                    component: <ProfilePanel profile={mappedProfile} />,
                  },
                ]
              : []
          }
          authUrls={{ loginUrl, googleLoginUrl, registerUrl }}
          trackingCallbacks={trackingCallbacks}
        />

        {hasNoMatches && (
          <Box background="body" borderRadius={['none', 'large']}>
            <NoSearchResults />
          </Box>
        )}

        {hasExactMatches && (
          <Box
            background="body"
            borderRadius={['none', 'large']}
            overflow="hidden"
          >
            <ProfileList
              profiles={exactMatchesProfiles}
              isAuthenticated={isAuthenticated}
              countText={t('X_MATCHING_PROFILES', {
                count: totalExactMatches,
              })}
              loginUrl={loginUrl}
              bottomActionBanner={
                showLoginInExactMatchList ? InListLoginPrompt : undefined
              }
              paginationProps={
                showPaginationInExactMatchList ? paginationProps : undefined
              }
              getProfileViewLink={getProfileViewLink}
              onProfileClick={handleProfileClick}
              trackingCallbacks={searchResultLoginTrackingCallbacks}
              onLoginNavigate={onLoginNavigate}
            />
          </Box>
        )}

        {hasPossibleMatches && (
          <Box
            background="body"
            borderRadius={['none', 'large']}
            overflow="hidden"
          >
            <ProfileList
              profiles={partialMatchesProfiles}
              isAuthenticated={isAuthenticated}
              countText={getPossibleMatchesCountText()}
              loginUrl={loginUrl}
              paginationProps={
                showPaginationInPossibleMatchList ? paginationProps : undefined
              }
              bottomActionBanner={
                showLoginInPossibleMatchList ? InListLoginPrompt : undefined
              }
              getProfileViewLink={getProfileViewLink}
              onProfileClick={handleProfileClick}
              trackingCallbacks={searchResultLoginTrackingCallbacks}
              onLoginNavigate={onLoginNavigate}
            />
          </Box>
        )}
      </Stack>
    </SkeletonLoader>
  );
};
